import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomButton from './CustomButton';
import CepMask from './InputMask/CepMask';
import { useRegister } from '../context/RegisterContext';
import { ViaCepResponse } from '../services/api';
import { EnderecoData } from '../types/RegisterType';
import './StepEndereco.css';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

const schema = yup.object({
  cep: yup
    .string()
    .required('Campo obrigatório')
    .test('cep', 'CEP inválido', val => (val?.replace(/\D/g, '') ?? '').length === 8),
  rua: yup.string().required('Campo obrigatório'),
  numero: yup.string().required('Campo obrigatório'),
  complemento: yup.string().default(''),
  bairro: yup.string().required('Campo obrigatório'),
  cidade: yup.string().required('Campo obrigatório'),
  uf: yup.string().required('Campo obrigatório').length(2, 'UF inválido'),
});

const StepEndereco: React.FC<Props> = ({ onNext, onBack }) => {
  const { formData, updateEndereco } = useRegister();
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState('');
  const cepTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const numberInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EnderecoData>({
    resolver: yupResolver(schema),
    defaultValues: formData.endereco,
  });

  const cepValue = watch('cep');

  useEffect(() => {
    const digits = cepValue?.replace(/\D/g, '') ?? '';
    if (digits.length === 8) {
      if (cepTimeoutRef.current) clearTimeout(cepTimeoutRef.current);
      cepTimeoutRef.current = setTimeout(() => fetchCepData(digits), 500);
    }
    return () => {
      if (cepTimeoutRef.current) clearTimeout(cepTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepValue]);

  const fetchCepData = async (digits: string) => {
    setLoadingCep(true);
    setCepError('');
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data: ViaCepResponse = await response.json();
      if (data.erro) {
        setCepError('CEP não encontrado');
        setValue('rua', '');
        setValue('bairro', '');
        setValue('cidade', '');
        setValue('uf', '');
      } else {
        setValue('rua', data.logradouro || '');
        setValue('bairro', data.bairro || '');
        setValue('cidade', data.localidade || '');
        setValue('uf', data.uf || '');
        numberInputRef.current?.focus();
      }
    } catch {
      setCepError('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(false);
    }
  };

  const onSubmit = (data: EnderecoData) => {
    updateEndereco(data);
    onNext();
  };

  return (
    <form className="step-endereco" onSubmit={handleSubmit(onSubmit)}>
      <h2 className="step-endereco-title">Endereço</h2>
      <p className="step-hint">Dica: Cadastre a unidade principal primeiro</p>

      <div className="form-row">
        <div className="form-group form-group-cep">
          <label htmlFor="cep" className="form-label">CEP</label>
          <div className="cep-input-wrapper">
            <Controller
              name="cep"
              control={control}
              render={({ field }) => (
                <CepMask
                  {...field}
                  className={`form-input ${errors.cep || cepError ? 'error' : ''} ${loadingCep ? 'loading' : ''}`}
                  placeholder="00000-000"
                />
              )}
            />
            {loadingCep && <span className="cep-loading">Buscando...</span>}
          </div>
          <a
            href="https://buscacepinter.correios.com.br/app/endereco/index.php"
            target="_blank"
            rel="noopener noreferrer"
            className="cep-helper-link"
          >
            Não sei meu CEP
          </a>
          {errors.cep && <span className="error-message">{errors.cep.message}</span>}
          {cepError && !errors.cep && <span className="error-message">{cepError}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="rua" className="form-label">Rua</label>
          <input
            id="rua"
            className={`form-input ${errors.rua ? 'error' : ''}`}
            placeholder="Nome da rua"
            disabled={loadingCep}
            {...register('rua')}
          />
          {errors.rua && <span className="error-message">{errors.rua.message}</span>}
        </div>
      </div>

      <div className="form-row form-row-two">
        <div className="form-group">
          <label htmlFor="numero" className="form-label">Número</label>
          <input
            id="numero"
            className={`form-input ${errors.numero ? 'error' : ''}`}
            placeholder="Número"
            ref={(el) => {
              register('numero').ref(el);
              numberInputRef.current = el;
            }}
            onChange={register('numero').onChange}
            onBlur={register('numero').onBlur}
            name={register('numero').name}
          />
          {errors.numero && <span className="error-message">{errors.numero.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="complemento" className="form-label">Complemento</label>
          <input
            id="complemento"
            className="form-input"
            placeholder="Complemento (opcional)"
            {...register('complemento')}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="bairro" className="form-label">Bairro</label>
          <input
            id="bairro"
            className={`form-input ${errors.bairro ? 'error' : ''}`}
            placeholder="Nome do bairro"
            disabled={loadingCep}
            {...register('bairro')}
          />
          {errors.bairro && <span className="error-message">{errors.bairro.message}</span>}
        </div>
      </div>

      <div className="form-row form-row-two">
        <div className="form-group">
          <label htmlFor="cidade" className="form-label">Cidade</label>
          <input
            id="cidade"
            className={`form-input ${errors.cidade ? 'error' : ''}`}
            placeholder="Nome da cidade"
            disabled={loadingCep}
            {...register('cidade')}
          />
          {errors.cidade && <span className="error-message">{errors.cidade.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="uf" className="form-label">UF</label>
          <input
            id="uf"
            className={`form-input ${errors.uf ? 'error' : ''}`}
            placeholder="SP"
            maxLength={2}
            disabled={loadingCep}
            {...register('uf', {
              onChange: e => {
                e.target.value = e.target.value.toUpperCase();
              },
            })}
          />
          {errors.uf && <span className="error-message">{errors.uf.message}</span>}
        </div>
      </div>

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton
          type="submit"
          variant="contained"
          customBackground="#46558D"
          sx={{ width: '150px' }}
        >
          Continuar
        </CustomButton>
      </div>
    </form>
  );
};

export default StepEndereco;
